import type { AnalyticsExportSamplePolicy } from '@/lib/analytics-export'
import type {
  AnalyticsExportRequest,
  AnalyticsExportRequestRangeId,
  AnalyticsExportRequestRow,
  AnalyticsExportRequestScopeId,
} from '@/lib/analytics-export-request'

export type AnalyticsExportResponsePreviewDownloadMode = 'browser_download' | 'signed_url_pending'

export type AnalyticsExportResponsePreviewInput = {
  request: AnalyticsExportRequest
  rowCount: number
  generatedAt: Date
  downloadMode: AnalyticsExportResponsePreviewDownloadMode
}

export type AnalyticsExportResponsePreviewPayload = {
  acceptedRange: AnalyticsExportRequestRangeId
  effectiveScope: AnalyticsExportRequestScopeId
  rowCount: number
  redactionPolicy: readonly AnalyticsExportSamplePolicy[]
  generatedAt: string
  downloadMode: AnalyticsExportResponsePreviewDownloadMode
}

export type AnalyticsExportResponsePreview = {
  payload: AnalyticsExportResponsePreviewPayload
  rows: AnalyticsExportRequestRow[]
  mockSafe: true
}

const forbiddenResponsePreviewKeys = new Set([
  'answer',
  'answers',
  'rawanswer',
  'rawanswers',
  'sample',
  'samples',
  'rawsample',
  'rawsamples',
  'rawrow',
  'rawrows',
  'rawresponserow',
  'rawresponserows',
  'exportrow',
  'exportrows',
  'email',
  'contactemail',
  'phone',
  'contactphone',
  'mobile',
  'address',
  'companyaddress',
  'taxid',
  'tax_id',
  'companytaxid',
  'businessaccountingno',
  'providerpayload',
  'd1rows',
  'rawd1rows',
])

const unsafeResponsePreviewValuePatterns = [
  /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i,
  /09\d{8}/,
  /0\d{1,2}-?\d{6,8}(?:#?\d+)?/,
  /[\u4e00-\u9fff]{0,8}(市|縣).{0,24}(路|街|巷|弄|號)/,
  /(^|[^\d])\d{8}([^\d]|$)/,
]

export function buildAnalyticsExportResponsePreview(
  input: AnalyticsExportResponsePreviewInput,
): AnalyticsExportResponsePreview {
  assertSafeAnalyticsExportResponsePreview(input)

  const payload: AnalyticsExportResponsePreviewPayload = {
    acceptedRange: input.request.payload.range,
    effectiveScope: input.request.payload.scope,
    rowCount: input.rowCount,
    redactionPolicy: input.request.payload.redactionPolicy,
    generatedAt: input.generatedAt.toISOString(),
    downloadMode: input.downloadMode,
  }

  return {
    mockSafe: true,
    payload,
    rows: [
      { field: 'acceptedRange', value: getRequestRowValue(input.request, 'range') },
      { field: 'effectiveScope', value: getRequestRowValue(input.request, 'scope') },
      { field: 'rowCount', value: String(input.rowCount) },
      { field: 'redactionPolicy', value: input.request.payload.redactionPolicy.join(' + ') },
      { field: 'generatedAt', value: payload.generatedAt },
      {
        field: 'downloadMode',
        value: getDownloadModeLabel(input.downloadMode),
      },
      {
        field: 'mock-safe 說明',
        value: 'server response metadata preview，不查 D1，不建立 signed URL，不寫入匯出檔',
      },
    ],
  }
}

function getRequestRowValue(request: AnalyticsExportRequest, field: string) {
  const rowValue = request.rows.find((row) => row.field === field)?.value
  if (rowValue) return rowValue
  if (field === 'range') return request.payload.range
  if (field === 'scope') return request.payload.scope
  return ''
}

function getDownloadModeLabel(downloadMode: AnalyticsExportResponsePreviewDownloadMode) {
  if (downloadMode === 'signed_url_pending') {
    return 'signed_url_pending（正式 API 才會建立 signed URL）'
  }
  return 'browser_download（mock-safe 前端下載，不建立 signed URL）'
}

function assertSafeAnalyticsExportResponsePreview(value: unknown, path = 'input') {
  if (Array.isArray(value)) {
    value.forEach((item, index) =>
      assertSafeAnalyticsExportResponsePreview(item, `${path}[${index}]`),
    )
    return
  }

  if (value && value instanceof Date) return

  if (value && typeof value === 'object') {
    for (const [key, nestedValue] of Object.entries(value)) {
      const normalizedKey = key.toLowerCase()
      if (normalizedKey === 'rows' && path !== 'input.request') {
        throw new Error(`Unsafe analytics export response preview: forbidden key ${path}.${key}`)
      }
      if (forbiddenResponsePreviewKeys.has(normalizedKey)) {
        throw new Error(`Unsafe analytics export response preview: forbidden key ${path}.${key}`)
      }
      assertSafeAnalyticsExportResponsePreview(nestedValue, `${path}.${key}`)
    }
    return
  }

  if (typeof value !== 'string') return

  if (unsafeResponsePreviewValuePatterns.some((pattern) => pattern.test(value))) {
    throw new Error(`Unsafe analytics export response preview: forbidden value at ${path}`)
  }
}
